import { ApolloError } from "@apollo/client";
import { addEdge, applyEdgeChanges, type EdgeAddChange, type EdgeChange } from "@xyflow/react";
import PubSub from "pubsub-js";
import { clone, filter, map } from "ramda";
import type { StateCreator } from "zustand";

import { pushRoute } from "@/app-utils/route.ts";
import { PubSubActions } from "@/global/pubsub-actions.ts";
import { type Connector, RetentionRuleEnum } from "@/graphql.ts";
import { notify } from "@/modules/notifications/notify";
import { WorkflowPubSubActions } from "@/modules/workflow/events/pubsub-actions.ts";
import {
  executeWorkflowMutation,
  resetWorkflowMutation,
  saveWorkflowEventMutation,
  stopWorkflowMutation,
} from "@/modules/workflow/graphql/queries.ts";
import type {
  WorkflowEditorStore,
  WorkflowExecutionSlice,
  WorkflowExecutionSliceState,
} from "@/modules/workflow/stores/workflow-editor-store-types";
import { hasInvalidConnections, validateAndFixConnections } from "@/modules/workflow/utils/connections.ts";
import { connectorToEdge, getWorkflowEventInputForSaving } from "@/modules/workflow/utils/generateWorkflow.ts";
import { ExtendedJobStatusEnum } from "@/modules/workflow/utils/types.ts";

export const initialExecutionSliceState: WorkflowExecutionSliceState = {
  workflowEvent: {
    retentionRule: RetentionRuleEnum.D,
    extendedExecutionStatus: ExtendedJobStatusEnum.Ready,
    executionUser: undefined,
    id: undefined,
    name: undefined,
    description: undefined,
    projectId: undefined,
    workflowId: undefined,
    defaultComputing: null,
  },
};

export const createExecutionSlice: StateCreator<WorkflowEditorStore, [], [], WorkflowExecutionSlice> = (set, get) => {
  PubSub.subscribe(PubSubActions.LOGOUT, () => {
    set(initialExecutionSliceState);
  });

  return {
    ...initialExecutionSliceState,
    async runWorkflowEvent(targetActivity?: string) {
      // Verify that the parameter settings are correct
      const anyActivityHasError = get().checkAllParameters();

      const executionId = get().workflowEvent.id;
      if (anyActivityHasError || !executionId || !get().workflowEvent?.retentionRule) {
        // abort
        notify.error(
          {
            title: "Cannot Execute Workflow",
            children:
              "Confirm all necessary activity parameters are valid and set and retention rule is correctly configured.",
          },
          { autoClose: false, toastId: `workflow-${executionId}-invalid` },
        );
        return;
      }

      const previousExtendedExecutionStatus = get().workflowEvent.extendedExecutionStatus;

      // reset the state and mark the job as inProgress
      set({
        workflowEvent: {
          ...get().workflowEvent,
          extendedExecutionStatus: ExtendedJobStatusEnum.Loading,
          lastRunningActivityName: null,
        },
      });

      const event = get().workflowEvent;

      if (event.name == undefined || event.projectId == undefined) {
        set({ workflowEvent: { ...get().workflowEvent, extendedExecutionStatus: previousExtendedExecutionStatus } });
        throw new Error("Session must have a name and a project must be selected");
      }

      // Save beforesetShowImportTemplateDialog(false)
      set({ isSavingWorkflow: true });

      const eventInputForSaving = validateAndFixConnections(
        getWorkflowEventInputForSaving([event, get().nodes, get().edges, get().parametersRecord, get().sampleTypeId]),
      );

      // validate connections
      const isInvalid = hasInvalidConnections(
        eventInputForSaving.config.activities,
        eventInputForSaving.config.connectors,
      );

      if (isInvalid) {
        // write back the updated state to the store
        const edges = map(
          (connector) => addEdge(connectorToEdge(connector as Connector), [])[0],
          eventInputForSaving.config.connectors,
        );
        const addEdgeChanges: EdgeChange[] = map((item) => ({ type: "add", item }) satisfies EdgeAddChange, edges);
        set({ edges: applyEdgeChanges(addEdgeChanges, []) });
      }

      const { data: saveData, error: saveError } = await saveWorkflowEventMutation({
        workflow: eventInputForSaving,
      });
      set({ isSavingWorkflow: false });

      if (saveError) {
        set({ workflowEvent: { ...get().workflowEvent, extendedExecutionStatus: previousExtendedExecutionStatus } });
        throw saveError;
      }

      const id = saveData?.workflow.saveWorkflowEvent.workflowEventId;

      if (id == undefined || id !== event.id) {
        set({ workflowEvent: { ...get().workflowEvent, extendedExecutionStatus: previousExtendedExecutionStatus } });
        throw new Error("Saving error");
      }

      const { data, error } = await executeWorkflowMutation({ workflowEventId: id, targetActivity });
      const exec = data?.workflow?.executeWorkflow;

      if (error) {
        set({ workflowEvent: { ...get().workflowEvent, extendedExecutionStatus: ExtendedJobStatusEnum.Error } });
        throw error;
      }

      if (exec) {
        const workflowEventId = exec.workflowEventId;
        const folderId = exec.config.outputFolder?.id;
        const activityConfigs = exec.config.activities; // just reading the output folders config
        const workflowEventName = exec.alias; // in case was set automatically

        set({
          workflowEvent: {
            ...get().workflowEvent,
            id: workflowEventId,
            name: workflowEventName,
            folderId,
          },
        });

        const nodes = clone(get().nodes);

        activityConfigs.map((activityConfig) => {
          const nodeIndex = nodes.findIndex((node) => node.id === activityConfig.identifier);
          nodes[nodeIndex].data.outputFolderId = activityConfig.outputFolder?.id;
        });
        set({ nodes });

        await pushRoute(`/processor/session/${workflowEventId}`);
      }
    },
    async terminateWorkflowEvent() {
      const workflowEventId = get().workflowEvent.id;

      set({ workflowEvent: { ...get().workflowEvent, extendedExecutionStatus: ExtendedJobStatusEnum.Stopping } });

      try {
        if (workflowEventId != undefined) await stopWorkflowMutation({ workflowEventId });
      } catch (error: unknown) {
        if (error instanceof ApolloError || error instanceof Error) {
          notify.error({
            title: "Workflow Killing Failed",
            children: error.message,
          });
        }
      } finally {
        set({
          workflowEvent: {
            ...get().workflowEvent,
          },
        });
      }
    },
    async wipeTemporaryData(nodeIds?: string[]) {
      const workflowEventId = get().workflowEvent.id;

      try {
        if (workflowEventId == undefined) return;

        const { data, error } = await resetWorkflowMutation({ workflowEventId, nodeIds });

        if (error) {
          throw error;
        }

        if (data == undefined) return;

        if (nodeIds?.length) {
          // Only a sub-branch was reset.
          // Act carefully: only set nodes to undefined, not to other statuses
          const activities = filter(
            (activity) => activity.status == undefined,
            data.workflow.resetWorkflow.config.activities,
          );

          for (const node of activities) {
            PubSub.publish(WorkflowPubSubActions.RELOAD_OUTPUT, node.identifier);
          }

          get().updateNodesMetadata(
            map((activity) => ({ nodeId: activity.identifier, data: { status: undefined } }), activities),
          );
        } else {
          // Reset all nodes statuses
          get().updateNodesMetadata(map((node) => ({ nodeId: node.id, data: { status: undefined } }), get().nodes));
          PubSub.publish(WorkflowPubSubActions.RELOAD_OUTPUT, null);
        }

        if (!nodeIds?.length) {
          get().updateWorkflowEvent({ executionUser: undefined });
          get().refreshWorkflowEventDetails();
        }
      } catch (error: unknown) {
        if (error instanceof ApolloError || error instanceof Error) {
          notify.error({
            title: `${nodeIds?.length ? "Activity" : "Workflow"} Reset Failed`,
            children: error.message,
          });
        }
      }
    },
    updateWorkflowEvent(data) {
      set({
        workflowEvent: { ...get().workflowEvent, ...data },
      });
    },
  };
};
